import './CompetencePage.css';
import { EEntityType, ELinks, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import type { ICompetenceSuitableEmployees } from '@sharedInterfaces/ICompetence';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import competenceGetSuggestedEmployees from '@src/APIs/graphQl/Competence/competenceGetSuggestedEmployees';
import getCompetence from '@src/APIs/graphQl/Competence/getCompetence';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import CardBox from '@src/App/Components/Cards/CardBox/CardBox';
import EmployeesCard from '@src/App/Components/Cards/EntityCards/Employees/EmployeesCard/EmployeesCard';
import OpportunitiesCard from '@src/App/Components/Cards/EntityCards/Opportunities/OpportunitiesCard/OpportunitiesCard';
import ProductsCard from '@src/App/Components/Cards/EntityCards/Products/ProductsCard/ProductsCard';
import RolesCard from '@src/App/Components/Cards/EntityCards/Roles/RolesCard/RolesCard';
import EntityPageInformation, {
    generateAcusitionEntityPageInformation,
} from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import { CompetenceDialog } from '@src/App/Components/Dialogs/CompetenceDialog/CompetenceDialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { EmployeeSuggestions } from '@src/App/Components/General/EmployeeSuggestion/EmployeeSuggestions/EmployeeSuggestions';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import { CompetenceLevelsInput } from '@src/App/Components/General/FormularElements/formsControls/inputs/CompetenceLevelsInput/CompetenceLevelsInput';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type Competence from '@src/Objects/Competence';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

interface CompetencePageProps {
    entity?: Competence;
    inDialog?: boolean;
}

/**
 * CompetencePage component.
 *
 * @param {CompetencePageProps} entity - The competence page props.
 * @param {boolean} inDialog - Indicates whether the component is rendered in a dialog.
 * @param {function} onChange - Handler function for the onChange event.
 * @param {function} onDelete - Handler function for the onDelete event.
 * @returns {JSX.Element} The rendered competence page component.
 */
function CompetencePage({ entity, inDialog }: CompetencePageProps) {
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const { id, tab } = useParams();
    const entityId = entity ? entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).competence;
    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);
    const entityState = useSelector((state: AppState) => state.competences.find((a) => a.id === entityId));

    const [suitableEmployees, setSuitableEmployees] = useState<ICompetenceSuitableEmployees>();
    const [loadingSuitableEmployees, setSuitableEmployeesLoading] = useState<boolean>(false);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);

    const loadSuitableEmployees = () => {
        if (loadingSuitableEmployees) return;
        setSuitableEmployeesLoading(true);
        competenceGetSuggestedEmployees(entityId, (details) => {
            details.levels.forEach((level) =>
                level.employees.sort((a, b) => {
                    return b.score - a.score;
                }),
            );
            setSuitableEmployees(details);
        }).catch((ex) => {
            setLoadingError(ex);
        });
    };

    useEffect(() => {
        if (inDialog) return;
        getCompetence(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, inDialog]);

    useEffect(() => {
        if (inDialog) return;
        if (entityId === -1) return;
        if (tab !== 'suitableEmployees') return;
        if (suitableEmployees) return;
        loadSuitableEmployees();
    }, [entity, tab]);

    let content;
    if (entityState) {
        switch (tab) {
            case 'suitableEmployees':
                content = (
                    <>
                        {suitableEmployees?.levels.map((level) => {
                            const levelName = entityState.levels[level.level - 1].title;
                            return (
                                <CardBox key={level.level} title={levelName}>
                                    <EmployeeSuggestions
                                        suggestedEmployees={level.employees}
                                        id={level.level.toString()}
                                    />
                                </CardBox>
                            );
                        })}
                    </>
                );
                break;
            default:
                content = (
                    <>
                        <EmployeesCard employees={entityState.employees} competenceLevels={entityState.levels} />
                        {readRoles && <RolesCard roles={entityState.roles} />}
                        <ProductsCard products={entityState.products} />
                        <OpportunitiesCard opportunities={entityState.opportunities} />
                        <CompetenceLevelsInput levels={entityState.levels} />
                    </>
                );
        }
    }

    const handleOpenEditDialog = async () => {
        return openDialog(<CompetenceDialog id="CompetenceDialog" competence={entityState} />);
    };
    return (
        <EntityPage
            inDialog={inDialog}
            loadingError={loadingError}
            entity={entityState}
            admin={permissions.admin}
            permissions={permissions.entities.Competencies}
            tabs={
                inDialog
                    ? undefined
                    : [
                          {
                              title: langStrings.oftenUsed.dashboard,
                              link: entityTypeToLink(entityId, EEntityType.COMPETENCE, 'dashboard'),
                          },
                          {
                              title: langStrings.suitableEmployees,
                              link: entityTypeToLink(entityId, EEntityType.COMPETENCE, 'suitableEmployees'),
                          },
                      ]
            }
            onDelete={() => {
                if (!inDialog) navigate(`/${ELinks.COMPETENCE}`);
            }}
            openEditDialog={handleOpenEditDialog}
            informations={
                entityState
                    ? [
                          entityState.description ? (
                              <EntityPageInformation title={langStrings.description} size={'full'}>
                                  <ExpandableText>
                                      <FormatedTextInput value={entityState.description} readonly={true} />
                                  </ExpandableText>
                              </EntityPageInformation>
                          ) : undefined,
                          generateAcusitionEntityPageInformation(entityState, langStrings),
                      ]
                    : []
            }
        >
            {content}
        </EntityPage>
    );
}

export default CompetencePage;
